import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getProviderMethod,
  isValidProviderMethod,
  PaymentMethod,
  PaymentProvider,
  ValidProviderMethod,
} from '@ca/common'
import { resolveProvider } from '@order/hooks/usePaymentMethods'

const translationKeys: Record<ValidProviderMethod, string> = {
  'stripe:terminal': 'terminal.default',
  'stripe:paylink': 'paylink.default',
  'payla:invoice': 'invoice.default',
  'sepa:monthly': 'installment.default',
  'unzer:monthly': 'installment.default',
  'cofidis:monthly': 'external.default',
  'tap:paylink': 'external.default',
  'alior:monthly': 'external.default',
  'cofidisApplication:monthly': 'external.default',
  'hasabe:terminal': 'terminal.default',
  'hasabe:paylink': 'external.default',
}

export default function usePaymentTranslations({
  method,
  provider,
}: {
  method: PaymentMethod
  provider: PaymentProvider | undefined
}) {
  const { t } = useTranslation(['payment', 'ui'])

  const providerResolved = useMemo(() => {
    const resolved = resolveProvider(method, provider)
    return resolved ?? 'stripe'
  }, [method, provider])

  const providerName = t(`payment:provider.${providerResolved}`)

  const key = useMemo(() => {
    const providerMethod = getProviderMethod(providerResolved, method)

    const translationKey = isValidProviderMethod(providerMethod)
      ? translationKeys[providerMethod]
      : translationKeys['stripe:terminal']

    return translationKey
  }, [method, providerResolved])

  return {
    provider: providerResolved,
    providerName,
    key,
  }
}
